/**
 * CreateOrganizationForm - Form for creating a new organization
 * Features slug validation, display name input, and preview
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { CreateOrganizationInput } from '../../types/organization';

interface CreateOrganizationFormProps {
  onSuccess: (organizationName: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const CreateOrganizationForm: React.FC<CreateOrganizationFormProps> = ({
  onSuccess,
  loading,
  setLoading
}) => {
  const { createOrganization, error, clearError } = useOrganization();
  const [formData, setFormData] = useState<CreateOrganizationInput>({
    name: '',
    displayName: ''
  });
  const [slugError, setSlugError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  // Generate URL-safe slug from display name
  const generateSlug = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Validate slug format
  const validateSlug = useCallback((slug: string): string => {
    if (!slug) {
      return 'Organization name is required';
    }

    if (slug.length < 3) {
      return 'Organization name must be at least 3 characters';
    }

    if (slug.length > 50) {
      return 'Organization name must be less than 50 characters';
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      return 'Organization name can only contain lowercase letters, numbers, and hyphens';
    }

    if (slug.startsWith('-') || slug.endsWith('-')) {
      return 'Organization name cannot start or end with a hyphen';
    }

    if (slug.includes('--')) {
      return 'Organization name cannot contain consecutive hyphens';
    }

    return '';
  }, []);

  // Update slug when display name changes
  useEffect(() => {
    if (formData.displayName && !formData.name) {
      const newSlug = generateSlug(formData.displayName);
      setFormData(prev => ({ ...prev, name: newSlug }));
    }
  }, [formData.displayName, formData.name]);

  // Validate slug when it changes
  useEffect(() => {
    const error = validateSlug(formData.name);
    setSlugError(error);
  }, [formData.name, validateSlug]);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    setFormError('');
  }, [formData, error, clearError]);

  const handleInputChange = (field: keyof CreateOrganizationInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlugChange = (value: string) => {
    // Only allow valid slug characters while typing
    const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    handleInputChange('name', cleanValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.displayName.trim()) {
      setFormError('Display name is required');
      return;
    }

    if (!formData.name.trim()) {
      setFormError('Organization name is required');
      return;
    }

    if (slugError) {
      setFormError(slugError);
      return;
    }

    setLoading(true);
    setFormError('');

    try {
      const response = await createOrganization({
        name: formData.name.trim(),
        displayName: formData.displayName.trim()
      });

      console.log('Organization created successfully:', response);
      onSuccess(response.organization.displayName);
    } catch (err: any) {
      console.error('Failed to create organization:', err);
      setFormError(err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const displayError = formError || error;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Display Name Field */}
      <div>
        <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
          Organization Display Name
        </label>
        <input
          id="displayName"
          type="text"
          value={formData.displayName}
          onChange={(e) => handleInputChange('displayName', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., Acme Corporation"
          disabled={loading}
          maxLength={100}
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          The full name of your organization as it will appear in the app.
        </p>
      </div>

      {/* Organization Name (Slug) Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Organization Name (URL)
        </label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => handleSlugChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            slugError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
          }`}
          placeholder="e.g., acme-corp"
          disabled={loading}
          maxLength={50}
          required
        />
        {slugError ? (
          <p className="text-sm text-red-600 mt-1">{slugError}</p>
        ) : (
          <p className="text-sm text-gray-500 mt-1">
            This will be used in URLs and must be unique. Only lowercase letters, numbers, and hyphens allowed.
          </p>
        )}
      </div>

      {/* Preview */}
      {formData.name && !slugError && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
          <p className="text-sm text-gray-600">
            Your organization will be: <span className="font-mono font-medium">{formData.name}</span>
          </p>
        </div>
      )}

      {/* Error Display */}
      {displayError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700">{displayError}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !formData.displayName.trim() || !formData.name.trim() || !!slugError}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating Organization...
          </div>
        ) : (
          'Create Organization'
        )}
      </button>
    </form>
  );
};